import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, X, Camera, Image, CheckCircle, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function UploadZone({ onFileSelect, isProcessing }) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      setPendingFile(file);
      setShowPaymentPrompt(true);
    }
  }, []);

  const handleChange = useCallback((e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPendingFile(file);
      setShowPaymentPrompt(true);
    }
  }, []);

  const handlePaymentStatusSelect = useCallback((status) => {
    if (pendingFile) {
      onFileSelect(pendingFile, status);
      setShowPaymentPrompt(false);
      setPendingFile(null);
    }
  }, [pendingFile, onFileSelect]);

  const cancelUpload = useCallback(() => {
    setShowPaymentPrompt(false);
    setPendingFile(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, []);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setPendingFile(null);
    setShowPaymentPrompt(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, []);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  // Check if device likely has camera (mobile/tablet)
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        id="file-upload"
        accept=".pdf,.png,.jpg,.jpeg,.bmp,.tif,.tiff,.gif,.heic,.heif"
        onChange={handleChange}
        className="hidden"
        disabled={isProcessing}
      />
      <input
        ref={cameraInputRef}
        type="file"
        id="camera-upload"
        accept="image/*"
        capture="environment"
        onChange={handleChange}
        className="hidden"
        disabled={isProcessing}
      />

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!isMobileDevice ? openFilePicker : undefined}
        className={`
          border-2 border-dashed rounded-2xl p-6 sm:p-12 text-center
          transition-all duration-300 cursor-pointer group
          ${dragActive
            ? 'border-indigo-500 bg-indigo-50/50 ring-2 ring-indigo-200 ring-offset-2'
            : 'border-slate-300 hover:border-indigo-500 hover:bg-indigo-50/30'
          }
          ${isProcessing ? 'opacity-50 pointer-events-none' : ''}
        `}
        data-testid="upload-zone"
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="w-14 sm:w-16 h-14 sm:h-16 rounded-full bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
            <Upload className="w-7 sm:w-8 h-7 sm:h-8 text-indigo-700" strokeWidth={2} />
          </div>
          
          {selectedFile ? (
            <div className="flex items-center space-x-2 sm:space-x-3 bg-white border border-slate-200 rounded-lg px-3 sm:px-4 py-2 sm:py-3 max-w-full">
              <FileText className="w-5 h-5 text-indigo-700 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-slate-700 truncate max-w-[150px] sm:max-w-[300px]">
                {selectedFile.name}
              </span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearFile();
                }}
                className="text-slate-400 hover:text-rose-600 transition-colors flex-shrink-0"
                data-testid="clear-file-button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div>
                <p className="text-base sm:text-lg font-medium text-slate-900 mb-1">
                  {isMobileDevice ? 'Upload your invoice' : 'Drop your invoice here'}
                </p>
                <p className="text-xs sm:text-sm text-slate-500">
                  {isMobileDevice ? 'Take a photo or choose from gallery' : 'or click to browse files'}
                </p>
              </div>

              {/* Mobile-optimized buttons */}
              {isMobileDevice && (
                <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openCamera();
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-700 text-white rounded-xl font-medium hover:bg-indigo-800 transition-colors active:scale-95"
                    data-testid="camera-button"
                  >
                    <Camera className="w-5 h-5" />
                    <span>Take Photo</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openFilePicker();
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white text-indigo-700 border-2 border-indigo-200 rounded-xl font-medium hover:bg-indigo-50 transition-colors active:scale-95"
                    data-testid="gallery-button"
                  >
                    <Image className="w-5 h-5" />
                    <span>Gallery</span>
                  </button>
                </div>
              )}

              <p className="text-xs text-slate-400 px-4">
                Supports PDF, PNG, JPG, HEIC • Max 10MB
              </p>
            </>
          )}

          {isProcessing && (
            <div className="flex items-center space-x-2 text-indigo-700">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-700"></div>
              <span className="text-sm font-medium">Processing invoice...</span>
            </div>
          )}
        </div>
      </div>

      {/* Payment Status Prompt Modal */}
      <AnimatePresence>
        {showPaymentPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={cancelUpload}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-sm shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                Is this invoice paid?
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {selectedFile?.name}
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handlePaymentStatusSelect('paid')}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
                  data-testid="mark-paid-btn"
                >
                  <CheckCircle className="w-5 h-5" />
                  Yes, already paid
                </button>
                <button
                  onClick={() => handlePaymentStatusSelect('unpaid')}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors"
                  data-testid="mark-unpaid-btn"
                >
                  <Clock className="w-5 h-5" />
                  No, pending payment
                </button>
                <button
                  onClick={cancelUpload}
                  className="px-4 py-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default UploadZone;